import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import RetroWindow from "@/components/RetroWindow";
import { PixelHeart, PixelSmiley, PixelStar, Tape } from "@/components/Sticker";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const { user, loading: authLoading, loginEmail, loginGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectAfter = () => {
    const fromLocation = location.state?.from;
    const from = fromLocation
      ? `${fromLocation.pathname || ""}${fromLocation.search || ""}${fromLocation.hash || ""}`
      : null;
    navigate(from || "/admin", { replace: true });
  };

  useEffect(() => {
    if (!authLoading && user) redirectAfter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginEmail(email, password);
      toast.success("Admin signed in.");
    } catch (err) {
      toast.error(humanizeAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = async () => {
    setLoading(true);
    try {
      await loginGoogle();
      toast.success("Signed in with Google");
    } catch (err) {
      toast.error(humanizeAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen retro-gradient-bg paper-grain flex items-center justify-center p-6 relative">
      <PixelStar className="absolute top-12 left-12 hidden md:block animate-float" />
      <PixelSmiley className="absolute bottom-16 right-10 hidden md:block animate-wiggle" />
      <PixelHeart className="absolute top-24 right-24 hidden md:block" />
      <div className="w-full max-w-md relative">
        <Tape className="-top-3 left-12" rotate={-8} color="#FFD84D" />
        <RetroWindow title="admin-login.exe" accent="pink">
          <div className="text-center mb-6">
            <Link to="/" className="font-pixel text-[10px] mb-2 inline-block">// NOTE CONSOLE</Link>
            <h1 className="font-heading text-3xl uppercase">Admin Login</h1>
            <p className="font-body text-sm text-retro-ink/70 mt-2">
              Sign in to view public order responses.
            </p>
          </div>

          <button
            type="button"
            onClick={onGoogle}
            disabled={loading}
            className="retro-btn w-full bg-white py-3 mb-5 flex items-center justify-center gap-2"
            data-testid="google-signin-btn"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="flex items-center gap-3 my-4">
            <div className="h-[2px] flex-1 bg-retro-ink/20" />
            <span className="font-pixel text-[9px] text-retro-ink/60">OR EMAIL</span>
            <div className="h-[2px] flex-1 bg-retro-ink/20" />
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            <Field icon={Mail} placeholder="Admin email" type="email" value={email} onChange={setEmail} testId="email-input" required />
            <Field icon={Lock} placeholder="Password" type="password" value={password} onChange={setPassword} testId="password-input" required />

            <button
              type="submit"
              disabled={loading}
              className="retro-btn w-full bg-retro-purple text-white py-3 flex items-center justify-center gap-2"
              data-testid="submit-auth-btn"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Login as Admin"}
            </button>
          </form>
        </RetroWindow>
      </div>
    </div>
  );
}

function Field({ icon: Icon, value, onChange, testId, ...rest }) {
  return (
    <label className="block">
      <div className="flex items-center gap-2 border-[2.5px] border-retro-ink rounded-lg bg-white px-3 py-2 focus-within:shadow-retro-sm transition-shadow">
        <Icon size={16} className="text-retro-ink/60" />
        <input
          {...rest}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 outline-none font-body text-sm bg-transparent placeholder:text-retro-ink/50"
          data-testid={testId}
        />
      </div>
    </label>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.6 15.2 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.1z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.2 0-9.6-3.6-11.2-8.4l-6.5 5C9.5 39.5 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4-4.1 5.3l6.2 5.2c-.4.4 6.6-4.8 6.6-14.5 0-1.3-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

function humanizeAuthError(err) {
  const code = err?.code || "";
  const map = {
    "auth/invalid-email": "That doesn't look like a valid email.",
    "auth/missing-email": "Please enter your email.",
    "auth/missing-password": "Please enter a password.",
    "auth/wrong-password": "Wrong password. Try again.",
    "auth/user-not-found": "No admin account with that email.",
    "auth/invalid-credential": "Email or password is incorrect.",
    "auth/popup-closed-by-user": "Google sign-in cancelled.",
    "auth/network-request-failed": "Network error. Check your connection.",
  };
  return map[code] || err?.message || "Something went wrong.";
}
